<!--
  Copyright 2023-2026 Bloomreach

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

    https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
  -->

<template>
  <span v-if="meta && meta.length > 0" style="display: none;" ref="head"/>
  <slot v-bind="$attrs" />
  <span v-if="meta && meta.length > 0" style="display: none;" ref="tail"/>
</template>

<script setup lang="ts">
import type { MetaCollection } from '@bloomreach/spa-sdk';
import { getCurrentInstance, inject, onBeforeUnmount, onBeforeUpdate, onMounted, onUpdated, ref, toRef, watch } from 'vue';
import { page$ } from '@/providerKeys';

const props = defineProps<{ meta?: MetaCollection }>();
const meta = toRef(() => props.meta);
let clear: ReturnType<MetaCollection['render']> | undefined;
const page = inject(page$, undefined);

const head = ref<HTMLSpanElement>();
const tail = ref<HTMLSpanElement>();

const injectMeta = () => {
  if (!head.value?.nextSibling || !tail.value) {
    return;
  }

  clear = meta.value?.render(head.value.nextSibling, tail.value);
  if (clear) page?.value?.sync();
};

const clearMeta = () => {
  if (!clear) return;
  clear();
  clear = undefined;
  page?.value?.sync();
};

watch(meta, () => getCurrentInstance()?.proxy?.$forceUpdate(), { deep: false });
onMounted(injectMeta);
onUpdated(injectMeta);
onBeforeUnmount(clearMeta);
onBeforeUpdate(clearMeta);
</script>
